import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  AlertTriangle, CreditCard, TrendingDown, FileText, Users,
  Loader2, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

/**
 * Admin Arrears Overview
 * Platform-wide arrears management
 */
export default function AdminArrears() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      const { data } = await api.get('/arrears/admin/overview');
      setOverview(data);
    } catch (error) {
      toast.error('Failed to load arrears overview');
    } finally {
      setLoading(false);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'grace_period': 'bg-yellow-100 text-yellow-800',
      'overdue': 'bg-orange-100 text-orange-800',
      'arrears': 'bg-red-100 text-red-800',
      'serious': 'bg-red-200 text-red-900',
      'legal_pending': 'bg-purple-100 text-purple-800'
    };
    return colors[stage] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl pb-20 lg:pb-6">
        {/* Header - mobile responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-display font-bold text-slate-900">Arrears</h1>
              <p className="text-sm text-slate-600">Platform-wide rent arrears</p>
            </div>
          </div>
          <Button onClick={loadOverview} variant="outline" className="w-full sm:w-auto">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats - mobile responsive grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Total Actions</p>
                  <p className="text-lg sm:text-2xl font-bold">{overview?.total_arrears_actions || 0}</p>
                </div>
                <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Total Amount</p>
                  <p className="text-lg sm:text-2xl font-bold text-red-600">£{(overview?.total_amount_in_arrears || 0).toLocaleString()}</p>
                </div>
                <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Payment Plans</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{overview?.active_payment_plans || 0}</p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500">Legal Notices</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">{overview?.legal_notices_issued || 0}</p>
                </div>
                <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* By Stage - mobile responsive */}
        <Card className="mb-6">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg">Arrears by Stage</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {Object.entries(overview?.by_stage || {}).map(([stage, count]) => (
                <div key={stage} className="text-center p-3 sm:p-4 bg-slate-50 rounded-lg">
                  <span className={`inline-block px-2 py-1 rounded text-xs ${getStageColor(stage)}`}>
                    {stage.replace('_', ' ')}
                  </span>
                  <p className="text-xl sm:text-2xl font-bold mt-2">{count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Info - mobile responsive */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg">Automated Arrears Workflow</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-3 sm:gap-4 p-3 bg-yellow-50 rounded-lg">
                <span className="w-7 h-7 sm:w-8 sm:h-8 bg-yellow-200 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">1-3</span>
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base">Grace Period (Days 1-3)</p>
                  <p className="text-xs sm:text-sm text-slate-500">Friendly email reminder sent</p>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 p-3 bg-orange-50 rounded-lg">
                <span className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-200 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">7</span>
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base">Formal Notice (Day 7)</p>
                  <p className="text-xs sm:text-sm text-slate-500">Formal overdue notice sent</p>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 p-3 bg-red-50 rounded-lg">
                <span className="w-7 h-7 sm:w-8 sm:h-8 bg-red-200 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">14</span>
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base">Final Warning (Day 14)</p>
                  <p className="text-xs sm:text-sm text-slate-500">Payment plan offered</p>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 p-3 bg-red-100 rounded-lg">
                <span className="w-7 h-7 sm:w-8 sm:h-8 bg-red-300 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">21</span>
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base">Letter Before Action</p>
                  <p className="text-xs sm:text-sm text-slate-500">Formal letter before legal</p>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 p-3 bg-purple-50 rounded-lg">
                <span className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-200 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">30+</span>
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base">Legal Notice (Day 30+)</p>
                  <p className="text-xs sm:text-sm text-slate-500">Section 8 notice generated</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
